import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import React from 'react';

import { IconButtonV2 } from 'packages/common-frontend/components/IconButton';
import { Icon } from '~/packages/common-frontend/components/icons';
import {
  errorToastConfig,
  successToastConfig,
} from '~/packages/common-frontend/constants';
import { GET_CREDIT_FACILITIES } from '~/packages/credit-facility/requests/requests.gql';
import { useUpdateCreditFacilityIsActiveMutation } from '~/packages/credit-facility/requests/requests.gql.generated';
import { GET_ORGANIZATION_IMPLEMENTATIONS_DETAILS } from '~/packages/implementations/requests/implementations.gql';
import { GetOrganizationImplementationsDetailsQueryGQLType } from '~/packages/implementations/requests/implementations.gql.generated';

// Type alias for implementation from the client query
type OrganizationImplementationFragmentGQLType = GetOrganizationImplementationsDetailsQueryGQLType['organization']['implementations'][0];

// Exports
type Props = {
  implementation: OrganizationImplementationFragmentGQLType;
  creditFacility: { id: string; name: string; isActive: boolean };
};
export const UpdateCreditFacilityIsActiveStatusButton = ({
  implementation,
  creditFacility,
}: Props) => {
  const [
    updateCreditFacilityIsActive,
    { loading },
  ] = useUpdateCreditFacilityIsActiveMutation({
    refetchQueries: [
      {
        query: GET_CREDIT_FACILITIES,
        variables: { organizationId: implementation.borrowerOrganization.id },
      },
      {
        query: GET_ORGANIZATION_IMPLEMENTATIONS_DETAILS,
        variables: { organizationId: implementation.ownerOrganization.id },
      },
    ],
  });
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const cancelRef = React.useRef(null);
  const currentIsActive = creditFacility.isActive;
  const action = currentIsActive ? 'Deactivate' : 'Reactivate';
  const label = `${action} credit facility`;
  const message = currentIsActive
    ? `Are you sure you want to deactivate the credit facility? It can be reactivated later.\n\n Note that deactivated facilities are not visible on the capital provider's organization page. Visit the borrower's organization page to reactivate.`
    : `Are you sure you want to reactivate the credit facility?`;
  return (
    <>
      <IconButtonV2
        aria-label={currentIsActive ? 'Deactivate' : 'Reactivate'}
        icon={
          currentIsActive ? <Icon icon="ban" /> : <Icon icon="arrows-rotate" />
        }
        label={currentIsActive ? 'Deactivate' : 'Reactivate'}
        onClick={() => onOpen()}
        variant="v2-ghost"
        colorScheme="gray"
        placement="bottom"
      />
      <AlertDialog
        leastDestructiveRef={cancelRef}
        isOpen={isOpen}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {label}
            </AlertDialogHeader>

            <AlertDialogBody whiteSpace="pre-line">{message}</AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef.current} onClick={onClose}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                isDisabled={loading}
                onClick={async () => {
                  try {
                    await updateCreditFacilityIsActive({
                      variables: {
                        creditFacilityId: creditFacility.id,
                        isActive: !currentIsActive,
                      },
                    });
                    toast({
                      ...successToastConfig,
                      title: `Successfully ${
                        currentIsActive ? 'deactivated' : 'reactivated'
                      } credit facility "${creditFacility.name}"`,
                    });
                    onClose();
                  } catch (err: any) {
                    toast({
                      ...errorToastConfig,
                      title: `Failed to ${
                        currentIsActive ? 'deactivate' : 'reactivate'
                      } credit facility "${creditFacility.name}": ${err}`,
                    });
                  }
                }}
                ml={3}
              >
                {action}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};
